import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getSupportById, replyToSupport } from "../../../services/SupportService";
import socket, { joinRestaurantRoom, joinSupportRoom } from "../../../services/socket";
import {
  getSupportContactMethodDisplay,
  getSupportIssueStartedDisplay,
  getSupportPriorityDisplay,
  getSupportSubcategoryLabel,
  getSupportTypeMeta,
} from "../../../contants/supportTypes";
import "./supportTicketViewPage.css";
import {
  SupportAttachmentLink,
  SupportMessageAttachments,
  SupportReplyAttachmentFields,
} from "./SupportReplyAttachmentFields";

const STATUS_META = {
  pending: { label: "Pending", tone: "pending" },
  in_progress: { label: "In progress", tone: "progress" },
  resolved: { label: "Resolved", tone: "resolved" },
};

const formatDateTime = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const getPersonName = (person) =>
  [person?.firstName, person?.lastName].filter(Boolean).join(" ").trim() || "Unknown";

const getAuthorInitials = (person) => {
  const first = person?.firstName?.[0] || "";
  const last = person?.lastName?.[0] || "";
  return `${first}${last}`.toUpperCase() || "AD";
};

const getThreadRoleLabel = (role) => {
  if (role === "superAdmin") return "Support";
  if (role === "responsable" || role === "resFranchise") return "Admin";
  return "Requester";
};

const isSupportSender = (role, author) =>
  role === "superAdmin" || author?.role === "superAdmin";

const getPriorityBadgeClass = (priority) => {
  if (priority === "critique" || priority === "haute") return "high";
  if (priority === "faible") return "low";
  return "medium";
};

const buildTicketTags = (support) => {
  if (!support) return [];
  const tags = new Set();
  const typeMeta = getSupportTypeMeta(support.type);
  if (typeMeta?.label) tags.add(typeMeta.label.toLowerCase());
  const subLabel = getSupportSubcategoryLabel(
    support.type,
    support.subcategory,
    support.customSubcategory
  );
  if (subLabel) tags.add(subLabel.toLowerCase());
  if (support.priority === "critique" || support.priority === "haute") {
    tags.add("high priority");
  }
  return Array.from(tags).slice(0, 4);
};

const getTicketRef = (id) => (id ? `#${String(id).slice(-6).toUpperCase()}` : "#------");

const formatThreadDateLabel = (value) => {
  if (!value) return "";
  const date = new Date(value);
  const now = new Date();
  const timePart = date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  if (date.toDateString() === now.toDateString()) return `TODAY · ${timePart}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return `YESTERDAY · ${timePart}`;
  return `${date.toLocaleDateString(undefined, { month: "short", day: "numeric" }).toUpperCase()} · ${timePart}`;
};

const buildThread = (support) => {
  if (!support) return [];
  const list = [
    {
      id: "original",
      type: "message",
      createdAt: support.createdAt,
      message: support.description || "",
      author: support.user,
      authorRole: support.user?.role || "responsable",
      side: "right",
      attachments: support.attachments || [],
    },
  ];

  (support.replies || [])
    .filter((reply) => reply.kind !== "internal_note")
    .forEach((reply) => {
      const fromSupport = isSupportSender(reply.authorRole, reply.author);
      list.push({
        id: reply._id || `${reply.createdAt}-${reply.message}`,
        type: "message",
        createdAt: reply.createdAt,
        message: reply.message,
        author: reply.author,
        authorRole: reply.authorRole || reply.author?.role,
        side: fromSupport ? "left" : "right",
        attachments: reply.attachments || [],
      });
    });

  return list;
};

const withDateSeparators = (items) => {
  const output = [];
  let dateKey = "";
  items.forEach((item) => {
    const key = item.createdAt ? new Date(item.createdAt).toDateString() : "unknown";
    if (key !== dateKey) {
      dateKey = key;
      output.push({
        type: "date",
        id: `date-${key}`,
        label: formatThreadDateLabel(item.createdAt),
      });
    }
    output.push(item);
  });
  return output;
};

export default function SupportTicketViewPage() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const threadRef = useRef(null);

  const [support, setSupport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [replyAttachments, setReplyAttachments] = useState([]);

  const replyKey = (reply) => {
    if (!reply) return "";
    if (reply._id) return String(reply._id);
    return `${reply.createdAt || ""}|${reply.message || ""}|${reply.authorRole || ""}`;
  };

  const applyIncomingSupport = (incoming) => {
    if (!incoming?._id) return;
    setSupport((prev) => {
      if (!prev) return incoming;
      const prevReplies = Array.isArray(prev.replies) ? prev.replies : [];
      const nextReplies = Array.isArray(incoming.replies) ? incoming.replies : [];
      const merged = { ...prev, ...incoming };
      if (nextReplies.length < prevReplies.length) merged.replies = prevReplies;
      return merged;
    });
  };

  const applyIncomingReply = (reply, extra = {}) => {
    if (!reply && extra.status == null) return;
    setSupport((prev) => {
      if (!prev) return prev;
      const replies = Array.isArray(prev.replies) ? [...prev.replies] : [];
      if (reply) {
        const key = replyKey(reply);
        if (!key || replies.some((item) => replyKey(item) === key)) {
          return extra.status ? { ...prev, ...extra } : prev;
        }
        replies.push(reply);
      }
      return { ...prev, ...extra, replies };
    });
  };

  const loadSupport = async () => {
    if (!ticketId) return;
    setLoading(true);
    try {
      const response = await getSupportById(ticketId);
      setSupport(response?.data || response);
    } catch (error) {
      toast.error(error.message || "Failed to load ticket");
      navigate("/my-support-requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSupport();
  }, [ticketId, navigate]);

  useEffect(() => {
    if (!ticketId) return undefined;

    const ticketMatches = (payload) => {
      const id =
        payload?.supportId ||
        payload?.support?._id ||
        payload?.extraData?.supportId;
      return id && String(id) === String(ticketId);
    };

    const refreshSilently = () => {
      if (document.visibilityState === "hidden") return;
      getSupportById(ticketId)
        .then((response) => applyIncomingSupport(response?.data || response))
        .catch(() => {});
    };

    const joinTicketRoom = () => joinSupportRoom(ticketId);

    const onSupportReply = (payload) => {
      if (!ticketMatches(payload)) return;
      if (payload?.support) applyIncomingSupport(payload.support);
      else if (payload?.reply) applyIncomingReply(payload.reply, { status: payload.status });
      else refreshSilently();
    };

    const onNewSupportReply = (notif) => {
      if (!ticketMatches(notif)) return;
      refreshSilently();
    };

    joinTicketRoom();
    if (!socket.connected) socket.connect();

    socket.on("support-reply", onSupportReply);
    socket.on("new-support-reply", onNewSupportReply);
    socket.on("connect", joinTicketRoom);
    socket.on("connect", refreshSilently);

    const pollId = window.setInterval(refreshSilently, 2500);
    document.addEventListener("visibilitychange", refreshSilently);

    return () => {
      window.clearInterval(pollId);
      document.removeEventListener("visibilitychange", refreshSilently);
      socket.off("support-reply", onSupportReply);
      socket.off("new-support-reply", onNewSupportReply);
      socket.off("connect", joinTicketRoom);
      socket.off("connect", refreshSilently);
    };
  }, [ticketId]);

  useEffect(() => {
    const restaurantId = support?.user?.restaurantFK;
    if (!restaurantId) return undefined;
    const join = () => joinRestaurantRoom(restaurantId);
    join();
    socket.on("connect", join);
    return () => socket.off("connect", join);
  }, [support?.user?.restaurantFK]);

  const threadItems = useMemo(() => withDateSeparators(buildThread(support)), [support]);
  const statusMeta = STATUS_META[support?.status || "pending"] || STATUS_META.pending;
  const subcategory =
    getSupportSubcategoryLabel(
      support?.type,
      support?.subcategory,
      support?.customSubcategory
    ) || "Support request";
  const restaurant = support?.user?.restaurantFK;
  const requesterName = getPersonName(support?.user);
  const ticketTags = useMemo(() => buildTicketTags(support), [support]);
  const typeMeta = getSupportTypeMeta(support?.type);
  const categoryLabel = typeMeta?.subcategoryLabel || typeMeta?.label || support?.type || "—";

  const participants = useMemo(() => {
    const map = new Map();
    if (support?.user?._id) map.set(String(support.user._id), support.user);
    (support?.replies || [])
      .filter((reply) => reply.kind !== "internal_note")
      .forEach((reply) => {
        if (reply?.author?._id) map.set(String(reply.author._id), reply.author);
      });
    return Array.from(map.values());
  }, [support]);

  useEffect(() => {
    if (!threadRef.current) return;
    threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [threadItems.length]);

  const handleSendReply = async () => {
    const value = message.trim();
    if ((!value && replyAttachments.length === 0) || !ticketId) return;
    setSending(true);
    try {
      const response = await replyToSupport(ticketId, value, replyAttachments);
      setSupport(response?.data || support);
      setMessage("");
      setReplyAttachments([]);
      toast.success("Reply sent");
    } catch (error) {
      toast.error(error?.message || "Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="stk-ticket-page__loading">Loading ticket...</div>;
  if (!support) return <div className="stk-ticket-page__loading">Ticket not found.</div>;

  return (
    <div className="stk-ticket-page">
      <section className="stk-ticket-main">
        <header className="stk-ticket-main__header">
          <div className="stk-ticket-main__header-top">
            <div className="stk-ticket-main__header-main">
              <div className="stk-ticket-main__crumb">
                Support <span>›</span> {restaurant?.nameRes || requesterName}{" "}
                <span>›</span> {getTicketRef(support._id)}
              </div>
              <div className="stk-ticket-main__title-row">
                <h1>{subcategory}</h1>
                <div
                  className={`stk-ticket-main__status stk-ticket-main__status--${statusMeta.tone}`}
                  aria-label={`Status: ${statusMeta.label}`}
                >
                  <span aria-hidden="true" />
                  {statusMeta.label}
                </div>
              </div>
              <p className="stk-ticket-main__meta">
                Requester <strong>{requesterName}</strong> · Opened{" "}
                {formatDateTime(support.createdAt)}
              </p>
            </div>
            <div className="stk-ticket-main__header-actions">
              <button
                type="button"
                className="stk-ticket-main__close"
                onClick={() => navigate("/my-support-requests")}
                aria-label="Close ticket"
              >
                <i className="fas fa-times" />
              </button>
            </div>
          </div>
        </header>

        <div className="stk-ticket-main__thread" ref={threadRef}>
          {threadItems.map((item) => {
            if (item.type === "date") {
              return (
                <div className="stk-ticket-main__date" key={item.id}>
                  {item.label}
                </div>
              );
            }

            const authorName = getPersonName(item.author);
            const roleLabel = getThreadRoleLabel(item.authorRole);
            const isRight = item.side === "right";
            const isSuper = isSupportSender(item.authorRole, item.author);
            return (
              <article
                className={`stk-ticket-main__msg stk-ticket-main__msg--${isRight ? "right" : "left"}`}
                key={item.id}
              >
                {!isRight && (
                  <span
                    className={`stk-ticket-main__avatar${
                      isSuper ? " stk-ticket-main__avatar--super" : ""
                    }`}
                  >
                    {getAuthorInitials(item.author)}
                  </span>
                )}
                <div className="stk-ticket-main__msg-body">
                  <div className="stk-ticket-main__msg-meta">
                    <strong>{authorName}</strong>
                    <span
                      className={`stk-ticket-main__role-badge stk-ticket-main__role-badge--${
                        isSuper ? "super" : "admin"
                      }`}
                    >
                      {roleLabel}
                    </span>
                    <em>{formatDateTime(item.createdAt)}</em>
                  </div>
                  <div
                    className={`stk-ticket-main__bubble stk-ticket-main__bubble--${
                      isRight ? "mine" : "theirs"
                    }`}
                  >
                    {item.message}
                    <SupportMessageAttachments
                      attachments={item.attachments}
                      ticketId={String(support._id)}
                      variant={isRight ? "mine" : "theirs"}
                    />
                  </div>
                </div>
                {isRight && (
                  <span className="stk-ticket-main__avatar">
                    {getAuthorInitials(item.author)}
                  </span>
                )}
              </article>
            );
          })}
        </div>

        <footer className="stk-ticket-main__composer">
          <div className="stk-ticket-main__compose-row">
            <div className="stk-ticket-main__compose-col">
              <textarea
                placeholder="Write a reply to support..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={sending}
              />
              <SupportReplyAttachmentFields
                attachments={replyAttachments}
                onChange={setReplyAttachments}
                disabled={sending}
              />
            </div>
            <button
              type="button"
              className="stk-ticket-main__send-btn"
              onClick={handleSendReply}
              disabled={sending || (!message.trim() && replyAttachments.length === 0)}
            >
              {sending ? "Sending..." : (
                <>
                  Send <i className="fas fa-chevron-right" aria-hidden="true" />
                </>
              )}
            </button>
          </div>
        </footer>
      </section>

      <aside className="stk-ticket-side">
        <div className="stk-ticket-side__card">
          <div className="stk-ticket-side__scroll">
            <div className="stk-ticket-side__user">
              <span className="stk-ticket-side__user-avatar">
                {getAuthorInitials(support.user)}
              </span>
              <h3>{requesterName}</h3>
              <p>Requester</p>
              {restaurant?.nameRes && (
                <span className="stk-ticket-side__restaurant-pill">
                  <i className="fas fa-store" aria-hidden="true" />
                  {restaurant.nameRes}
                </span>
              )}
            </div>

            <div className="stk-ticket-side__section">
              <h4>Ticket details</h4>
              <ul className="stk-ticket-side__detail-list">
                <li>
                  <span className="stk-ticket-side__detail-label">
                    <i className="fas fa-flag" aria-hidden="true" />
                    Priority
                  </span>
                  <span
                    className={`stk-ticket-side__priority-badge stk-ticket-side__priority-badge--${getPriorityBadgeClass(
                      support.priority
                    )}`}
                  >
                    {getSupportPriorityDisplay(support.priority)}
                  </span>
                </li>
                <li>
                  <span className="stk-ticket-side__detail-label">
                    <i className="fas fa-tags" aria-hidden="true" />
                    Category
                  </span>
                  <span className="stk-ticket-side__detail-value">{categoryLabel}</span>
                </li>
                <li>
                  <span className="stk-ticket-side__detail-label">
                    <i className="fas fa-comment-dots" aria-hidden="true" />
                    Contact
                  </span>
                  <span className="stk-ticket-side__detail-value">
                    {getSupportContactMethodDisplay(support.preferredContactMethod)}
                  </span>
                </li>
                <li>
                  <span className="stk-ticket-side__detail-label">
                    <i className="fas fa-clock" aria-hidden="true" />
                    Issue started
                  </span>
                  <span className="stk-ticket-side__detail-value">
                    {getSupportIssueStartedDisplay(support.issueStarted)}
                  </span>
                </li>
                <li>
                  <span className="stk-ticket-side__detail-label">
                    <i className="fas fa-paperclip" aria-hidden="true" />
                    Files
                  </span>
                  <span className="stk-ticket-side__detail-value">
                    {support.attachments?.length || 0}
                  </span>
                </li>
              </ul>
            </div>

            {support.attachments?.length > 0 && (
              <div className="stk-ticket-side__section">
                <h4>Attachments</h4>
                <ul className="stk-ticket-side__attachments">
                  {support.attachments.map((file) => (
                    <li key={file.filename || file.originalName}>
                      <SupportAttachmentLink
                        ticketId={String(support._id)}
                        file={file}
                        attachments={support.attachments}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="stk-ticket-side__section">
              <h4>Participants</h4>
              <ul className="stk-ticket-side__participants">
                {participants.map((item) => (
                  <li key={item._id || item.email}>
                    <span className={item.role === "superAdmin" ? "is-super" : ""}>
                      {getAuthorInitials(item)}
                    </span>
                    <div>
                      <strong>{getPersonName(item)}</strong>
                      <small>{getThreadRoleLabel(item.role)}</small>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {ticketTags.length > 0 && (
              <div className="stk-ticket-side__section">
                <h4>Tags</h4>
                <div className="stk-ticket-side__tags">
                  {ticketTags.map((tag) => (
                    <span className="stk-ticket-side__tag" key={tag}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="stk-ticket-side__footer">
            <button
              type="button"
              className="stk-ticket-side__back-btn stk-ticket-side__back-btn--solo"
              onClick={() => navigate("/my-support-requests")}
            >
              Back to my requests
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}
